import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { getAdminProfilePicture, getAdminBirthday } from '../services/publicProfileService';
import { formatUptime, formatServiceLifeRemaining } from '../utils/uptimeCalculator';
import StatusTimeline from './StatusTimeline';
import './PublicStatusPage.css';

const PublicStatusPage = () => {
  const [metrics, setMetrics] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [adminProfilePicture, setAdminProfilePicture] = useState(null);
  const [adminBirthday, setAdminBirthday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(true);
  const [profilePictureLoaded, setProfilePictureLoaded] = useState(false);
  const [showShimmer, setShowShimmer] = useState(true);

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch metrics (public read access)
      let metricsQuery = query(collection(db, 'metrics'), orderBy('order', 'asc'));
      let metricsSnapshot;
      
      try {
        metricsSnapshot = await getDocs(metricsQuery);
      } catch (orderError) {
        // If orderBy fails, fall back to createdAt ordering
        console.log('Order field not available, falling back to createdAt ordering');
        metricsQuery = query(collection(db, 'metrics'), orderBy('createdAt', 'desc'));
        metricsSnapshot = await getDocs(metricsQuery);
      }
      
      const metricsData = metricsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch log entries (public read access)
      const logEntriesQuery = query(collection(db, 'logEntries'), orderBy('date', 'desc'));
      const logEntriesSnapshot = await getDocs(logEntriesQuery);
      const logEntriesData = logEntriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch admin profile picture and birthday
      const profilePicture = await getAdminProfilePicture();
      const birthday = await getAdminBirthday();

      setMetrics(metricsData);
      setLogEntries(logEntriesData);
      setAdminProfilePicture(profilePicture);
      setAdminBirthday(birthday);
      setProfilePictureLoading(false);
      
      // If there's no profile picture, we're done loading
      if (!profilePicture) {
        setProfilePictureLoaded(true);
        setShowShimmer(false);
      } else {
        // For cached images, we can be more aggressive about skipping shimmer
        // Check if the image URL is the same as what we had before
        const lastImageURL = localStorage.getItem('lastProfilePictureURL');
        
        if (lastImageURL === profilePicture) {
          // Same image as before, likely cached - skip shimmer
          setShowShimmer(false);
          setProfilePictureLoaded(true);
        } else {
          // New or different image, show shimmer while loading
          setShowShimmer(true);
          localStorage.setItem('lastProfilePictureURL', profilePicture);
          
          // Check if image is already cached by trying to load it
          const img = new Image();
          img.onload = () => {
            setShowShimmer(false);
            setProfilePictureLoaded(true);
          };
          img.onerror = () => {
            // Keep shimmer if there's an error
            setShowShimmer(true);
          };
          img.src = profilePicture;
        }
      }
    } catch (err) {
      console.error('Error fetching public data:', err);
      setError('Failed to load status data');
    } finally {
      setLoading(false);
    }
  };

  const getLogEntriesForMetric = (metricId) => {
    return logEntries.filter(entry => entry.metricId === metricId);
  };

  const getOverallStatus = () => {
    if (metrics.length === 0) return 'No data';
    
    // Use local time instead of UTC to match the date picker
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    
    const todayEntries = logEntries.filter(entry => entry.date === todayStr);
    
    // Check if ALL metrics are logged for today
    const allMetricsLoggedToday = todayEntries.length === metrics.length;
    
      // Debug logging
      console.log('Status banner debug:');
      console.log('Today string (local):', todayStr);
      console.log('Today entries:', todayEntries);
      console.log('All log entries:', logEntries);
      console.log('Metrics count:', metrics.length);
      console.log('Today entries count:', todayEntries.length);
      console.log('All metrics logged today:', allMetricsLoggedToday);
      
      // Debug: Check user IDs in log entries
      const userIds = [...new Set(logEntries.map(entry => entry.userId))];
      console.log('User IDs found in log entries:', userIds);
    
    // Log each metric and its today entry
    metrics.forEach(metric => {
      const metricTodayEntry = todayEntries.find(entry => entry.metricId === metric.id);
      console.log(`Metric "${metric.name}" (${metric.id}):`, metricTodayEntry ? 'HAS today entry' : 'NO today entry');
    });
    
    if (todayEntries.length > 0) {
      // Some or all metrics logged today - show today's status
      const hasOutage = todayEntries.some(entry => entry.status === 'outage');
      const hasDegraded = todayEntries.some(entry => entry.status === 'degraded');
      
      if (allMetricsLoggedToday) {
        // All metrics logged today
        if (hasOutage) return 'Some systems experiencing outages';
        if (hasDegraded) return 'Some systems degraded';
        return 'All systems operational';
      } else {
        // Partial metrics logged today
        const loggedCount = todayEntries.length;
        const totalCount = metrics.length;
        if (hasOutage) return `Some systems experiencing outages (${loggedCount}/${totalCount} metrics updated today)`;
        if (hasDegraded) return `Some systems degraded (${loggedCount}/${totalCount} metrics updated today)`;
        return `All systems operational (${loggedCount}/${totalCount} metrics updated today)`;
      }
    }
    
    // Not all metrics logged today - check yesterday's status
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayYear = yesterday.getFullYear();
    const yesterdayMonth = String(yesterday.getMonth() + 1).padStart(2, '0');
    const yesterdayDay = String(yesterday.getDate()).padStart(2, '0');
    const yesterdayStr = `${yesterdayYear}-${yesterdayMonth}-${yesterdayDay}`;
    const yesterdayEntries = logEntries.filter(entry => entry.date === yesterdayStr);
    
    const allMetricsLoggedYesterday = yesterdayEntries.length === metrics.length;
    const someMetricsLoggedYesterday = yesterdayEntries.length > 0;
    
    if (allMetricsLoggedYesterday) {
      // Yesterday had all metrics logged
      const hasOutage = yesterdayEntries.some(entry => entry.status === 'outage');
      const hasDegraded = yesterdayEntries.some(entry => entry.status === 'degraded');
      
      if (hasOutage) return 'Awaiting updates for today. Yesterday some systems were experiencing outages.';
      if (hasDegraded) return 'Awaiting updates for today. Yesterday some systems were degraded.';
      return 'Awaiting updates for today. Yesterday all systems were operational.';
    }
    
    if (someMetricsLoggedYesterday) {
      // Yesterday had partial logs
      return 'Awaiting updates for today. Yesterday\'s logs were incomplete.';
    }
    
    // Yesterday had no logs - count consecutive days with no logs
    const consecutiveDaysWithNoLogs = getConsecutiveDaysWithNoLogs(yesterdayStr);
    const lastLoggedDay = getLastLoggedDay(yesterdayStr);
    
    if (consecutiveDaysWithNoLogs === 1) {
      return 'Awaiting updates for today. No response from georgekutti yesterday.';
    }
    
    let statusText = `Awaiting updates for today. No response from georgekutti for the past ${consecutiveDaysWithNoLogs} days.`;
    
    if (lastLoggedDay) {
      const lastLoggedEntries = logEntries.filter(entry => entry.date === lastLoggedDay);
      const lastStatus = getStatusSummary(lastLoggedEntries);
      const formattedDate = new Date(lastLoggedDay).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      statusText += ` Last recorded status on ${formattedDate}: ${lastStatus}.`;
    }
    
    return statusText;
  };

  const getConsecutiveDaysWithNoLogs = (startDate) => {
    let count = 0;
    let currentDate = new Date(startDate);
    
    while (count < 30) { // Limit to 30 days to prevent infinite loops
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const dayEntries = logEntries.filter(entry => entry.date === dateStr);
      
      if (dayEntries.length > 0) {
        break;
      }
      
      count++;
      currentDate.setDate(currentDate.getDate() - 1);
    }
    
    return count;
  };

  const getLastLoggedDay = (beforeDate) => {
    let currentDate = new Date(beforeDate);
    
    for (let i = 0; i < 90; i++) { // Look back up to 90 days
      currentDate.setDate(currentDate.getDate() - 1);
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const dayEntries = logEntries.filter(entry => entry.date === dateStr);
      
      if (dayEntries.length > 0) {
        return dateStr;
      }
    }
    
    return null;
  };

  const getStatusSummary = (entries) => {
    if (entries.length === 0) return 'no data';
    
    const hasOutage = entries.some(entry => entry.status === 'outage');
    const hasDegraded = entries.some(entry => entry.status === 'degraded');
    
    if (hasOutage) return 'some systems experiencing outages';
    if (hasDegraded) return 'some systems degraded';
    return 'all systems operational';
  };

  const getLastDataUpdateTime = () => {
    if (logEntries.length === 0) {
      return 'No data available';
    }
    
    // Find the most recent log entry based on updatedAt timestamp
    const mostRecentEntry = logEntries.reduce((latest, entry) => {
      const entryTime = entry.updatedAt ? entry.updatedAt.toDate() : entry.createdAt.toDate();
      const latestTime = latest.updatedAt ? latest.updatedAt.toDate() : latest.createdAt.toDate();
      
      return entryTime > latestTime ? entry : latest;
    });
    
    const lastUpdateTime = mostRecentEntry.updatedAt ? 
      mostRecentEntry.updatedAt.toDate() : 
      mostRecentEntry.createdAt.toDate();
    
    return lastUpdateTime.toLocaleString();
  };

  if (loading) {
    return (
      <div className="public-status-page">
        <div className="public-header">
          <div className="public-avatar">
            <div className="public-avatar-shimmer"></div>
          </div>
          <h1>georgekutti's status</h1>
        </div>
        <div className="loading-message">
          <p>Loading status data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-status-page">
        <div className="public-header">
          <div className="public-avatar">
            <div className="public-avatar-shimmer"></div>
          </div>
          <h1>georgekutti's status</h1>
        </div>
        <div className="error-message">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-status-page">
      <div className="public-header">
        <div className="public-avatar">
          {profilePictureLoading ? (
            <div className="public-avatar-shimmer"></div>
          ) : adminProfilePicture ? (
            <>
              {showShimmer && <div className="public-avatar-shimmer"></div>}
              <a 
                href={adminProfilePicture} 
                target="_blank" 
                rel="noopener noreferrer"
                className="public-avatar-link"
                title="To remind myself of what I enjoyed doing as a child — Amma took this while we were playing."
              >
                <img 
                  src={adminProfilePicture} 
                  alt="Profile" 
                  className={`public-avatar-image ${profilePictureLoaded ? 'loaded' : ''}`}
                  title="To remind myself of what I enjoyed doing as a child — Amma took this while we were playing."
                  onLoad={() => {
                    setProfilePictureLoaded(true);
                    setShowShimmer(false);
                  }}
                  loading="eager"
                  decoding="async"
                />
              </a>
            </>
          ) : (
            <div className="public-avatar-placeholder"></div>
          )}
        </div>
        <h1>georgekutti's status</h1>
        <p className="public-subtitle">{getOverallStatus()}</p>
      </div>

      <div className="public-metrics">
        {metrics.length === 0 ? (
          <div className="no-metrics">
            <p>No metrics available</p>
          </div>
        ) : (
          metrics.map(metric => (
            <div key={metric.id} className="public-metric">
              <StatusTimeline
                logEntries={getLogEntriesForMetric(metric.id)}
                metric={metric}
              />
            </div>
          ))
        )}
      </div>

      <div className="public-footer">
        {adminBirthday && (
          <p className="public-uptime">
            <span>Time since initial boot:</span>
            <span>{formatUptime(adminBirthday)}</span>
          </p>
        )}
        {adminBirthday && (
          <p className="public-service-life">
            <span>Expected service life remaining:</span>
            <span>{formatServiceLifeRemaining(adminBirthday, 70)}</span>
          </p>
        )}
        <div className="public-footer-divider"></div>
        <p>Last updated: {getLastDataUpdateTime()}</p>
      </div>
    </div>
  );
};

export default PublicStatusPage;
